/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Organization Settings Service
 * ============================================================================
 *
 * Service for managing organization-specific settings.
 * Allows tenants to override platform defaults.
 *
 * ============================================================================
 */

import apiClient from '../api/client';
import {
  OrganizationSettingsDto,
  UpdateOrganizationSettingsRequest,
  SettingDefinition,
} from '@/types/superadmin.types';
import { ApiResponse } from '@/types/api.types';

const BASE_URL = '/admin/organizations';

/**
 * Get all settings for an organization
 */
export async function getOrganizationSettings(orgId: string): Promise<OrganizationSettingsDto> {
  const response = await apiClient.get<ApiResponse<OrganizationSettingsDto>>(
    `${BASE_URL}/${orgId}/settings`
  );
  return response.data.data;
}

/**
 * Update organization settings (batch update)
 */
export async function updateOrganizationSettings(
  orgId: string,
  request: UpdateOrganizationSettingsRequest
): Promise<OrganizationSettingsDto> {
  const response = await apiClient.put<ApiResponse<OrganizationSettingsDto>>(
    `${BASE_URL}/${orgId}/settings`,
    request
  );
  return response.data.data;
}

/**
 * Reset all organization settings to platform defaults
 */
export async function resetOrganizationSettings(orgId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${orgId}/settings`);
}

/**
 * Get a single setting value
 */
export async function getOrganizationSetting(
  orgId: string,
  key: string
): Promise<{ key: string; value: string }> {
  const response = await apiClient.get<ApiResponse<{ key: string; value: string }>>(
    `${BASE_URL}/${orgId}/settings/${key}`
  );
  return response.data.data;
}

/**
 * Get available setting definitions
 */
export async function getAvailableSettings(): Promise<SettingDefinition[]> {
  // This endpoint doesn't need orgId, but follows the pattern
  const response = await apiClient.get<ApiResponse<SettingDefinition[]>>(
    `${BASE_URL}/settings/available`
  );
  return response.data.data;
}

export const organizationSettingsService = {
  getSettings: getOrganizationSettings,
  updateSettings: updateOrganizationSettings,
  resetSettings: resetOrganizationSettings,
  getSetting: getOrganizationSetting,
  getAvailableSettings,
};
